import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { Number3 } from '@abyss/3d-viewer';
import { DialogContent, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import {
  StyledDialog,
  ImageNumbering,
  CurrentPoiName,
  DialogContentStyle,
  MainContentContainer,
  PoiNavigationContainer,
  ImgNavButton,
} from './styles';
import { abyssColors } from '@/theme/colors';
import { DialogHeader } from './DialogHeader';
import { DetailsSidebar } from './DetailsSidebar';
import {
  useGetStructureForInspectionDataLoaderQuery,
  GenerateImageInput,
  FileTypes,
} from '@/__generated__/graphql';
import * as state from '@/components/Analysis/state';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useFoundViewpoints } from '@/components/Analysis/modules/pointOfInterest/PointOfInterestTab/SelectedPoiContent/hooks/useFoundViewpoints';
import { generatePoiDisplayName } from '@/components/Analysis/modules/pointOfInterest/PointOfInterestTab/SelectedPoiContent/utils';
import { thumbnailInputFromLookAt } from '@/components/Analysis/Viewer/Drawers/FindViewpointsTool/ViewpointThumbnail';
import { useGenerateImage } from '@/components/Analysis/modules/thumbnails';
import { useImageZoomAndDrag } from '@/hooks/useImageZoomAndDrag';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import {
  poiNavigationActionsState,
  selectedPoiForPreviewState,
  reviewScreenPoisState,
} from '../../../state';
import { MainImageSection } from './MainImageSection';
import { ThumbnailList } from './ThumbnailList';

type ImageType = 'primary' | 'attachment' | 'viewpoint';

interface ImageWithId {
  src: string;
  id: string;
  name?: string;
  viewpointId?: string;
  isLoading?: boolean;
  type?: ImageType;
}

interface ImagePreviewDialogProps {
  open: boolean;
  onClose: () => void;
  currentIndex: number;
  onChangeImage: (index: number) => void;
  initialImageId?: string;
}

export const ImagePreviewDialog: React.FC<ImagePreviewDialogProps> = ({
  open,
  onClose,
  currentIndex,
  onChangeImage,
  initialImageId,
}) => {
  const [viewpointImages, setViewpointImages] = useState<ImageWithId[]>([]);
  const [mainImageLoading, setMainImageLoading] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const setInspectionMetadata = useSetAtom(state.inspectionMetadata);
  const selectedPoiForPreview = useAtomValue(selectedPoiForPreviewState);
  const allPois = useAtomValue(reviewScreenPoisState);
  const setPoiNavigationActions = useSetAtom(poiNavigationActionsState);
  const structureId = useGetStructureId();

  const currentPoi = selectedPoiForPreview;

  const poiName = generatePoiDisplayName({
    name: currentPoi?.name,
    poiId: currentPoi?.poiId,
    poiType: currentPoi?.type,
  });

  const { imageTransform, imageEventHandlers, handleZoom, resetImageState, canZoomIn, canZoomOut } =
    useImageZoomAndDrag({
      maxScale: 3,
      minScale: 1,
      scaleStep: 0.5,
      xLimitMultiplier: 200,
      yLimitMultiplier: 150,
    });

  const { data: structureData } = useGetStructureForInspectionDataLoaderQuery({
    variables: { structureId },
    fetchPolicy: 'cache-first',
    skip: !structureId,
  });

  const poiCenterPoint = useMemo(() => {
    const { x, y, z } = currentPoi?.centerPoint3d ?? {};
    const centerPoint3dExists = x !== undefined && y !== undefined && z !== undefined;
    return centerPoint3dExists ? ([x, y, z] as Number3) : undefined;
  }, [currentPoi?.centerPoint3d]);

  const { foundViewpoints } = useFoundViewpoints(poiCenterPoint);
  const { fetchCropFromSpherical } = useGenerateImage();

  const fileData = useMemo(() => {
    return (
      currentPoi?.files
        ?.filter((file) => file.type === FileTypes.Png || file.type === FileTypes.Jpeg)
        .map((file) => ({
          path: file.resourcePath,
          name: file.name || 'Attachment',
        })) || []
    );
  }, [currentPoi?.files]);

  useEffect(() => {
    if (structureData?.structure) {
      setInspectionMetadata(structureData.structure);
    }
  }, [structureData?.structure, setInspectionMetadata]);

  useEffect(() => {
    const fetchViewpointImages = async () => {
      if (!foundViewpoints?.length) {
        setViewpointImages([]);
        return;
      }

      const viewpointPlaceholders: ImageWithId[] = foundViewpoints.map((viewpoint) => ({
        id: viewpoint.id,
        name: viewpoint.name,
        viewpointId: viewpoint.id,
        src: '',
        isLoading: true,
        type: 'viewpoint',
      }));
      setViewpointImages(viewpointPlaceholders);

      const imageFetchPromises = foundViewpoints.map(async (viewpoint) => {
        const input = thumbnailInputFromLookAt(viewpoint, poiCenterPoint) as GenerateImageInput;
        if (!input) return { id: viewpoint.id, src: '', isLoading: false };

        try {
          const imageData = await fetchCropFromSpherical(input);
          const src = imageData ? `data:image/jpeg;base64,${imageData}` : '';
          return { id: viewpoint.id, src, isLoading: false };
        } catch {
          return { id: viewpoint.id, src: '', isLoading: false };
        }
      });

      const resolvedImages = await Promise.all(imageFetchPromises);

      setViewpointImages((prev) =>
        prev.map((img) => {
          const updatedViewpoints = resolvedImages.find((u) => u.id === img.id);
          return updatedViewpoints ? { ...img, ...updatedViewpoints } : img;
        })
      );
    };

    fetchViewpointImages();
  }, [foundViewpoints, poiCenterPoint, fetchCropFromSpherical]);

  const imagesByCategory = useMemo(
    () => ({
      primary: currentPoi?.screenshotPaths?.viewpoint
        ? [
          {
            src: getCloudfrontUrl(currentPoi.screenshotPaths.viewpoint),
            id: 'primary',
            name: currentPoi?.location?.name || 'Primary Image',
            isLoading: false,
            type: 'primary',
          },
        ]
        : [],
      viewpoints: viewpointImages,
      attached: fileData.map((file) => ({
        src: getCloudfrontUrl(file.path),
        id: file.path,
        name: file.name,
        isLoading: false,
        type: 'attachment',
      })),
    }),
    [currentPoi?.screenshotPaths?.viewpoint, currentPoi?.location?.name, fileData, viewpointImages]
  );

  const imagesWithIds = useMemo(
    () => [
      ...imagesByCategory.primary,
      ...imagesByCategory.viewpoints,
      ...imagesByCategory.attached,
    ],
    [imagesByCategory]
  );

  useEffect(() => {
    if (open && !hasInitialized && imagesWithIds.length > 0) {
      if (initialImageId) {
        const targetIndex = imagesWithIds.findIndex((img) => img.id === initialImageId);
        if (targetIndex !== -1) {
          onChangeImage(targetIndex);
        } else {
          onChangeImage(0);
        }
      } else {
        onChangeImage(0);
      }
      resetImageState();
      setHasInitialized(true);
      setMainImageLoading(false);
    }
  }, [open, hasInitialized, initialImageId, imagesWithIds, onChangeImage, resetImageState]);

  useEffect(() => {
    if (!open || currentPoi?.id) {
      setHasInitialized(false);
    }
  }, [open, currentPoi?.id]);

  const currentImage = imagesWithIds[currentIndex] || imagesWithIds[0];

  const handleImageNavigation = useCallback(
    (direction: 'prev' | 'next') => () => {
      const total = imagesWithIds.length;
      const newIndex =
        direction === 'prev'
          ? currentIndex > 0
            ? currentIndex - 1
            : total - 1
          : currentIndex < total - 1
            ? currentIndex + 1
            : 0;

      onChangeImage(newIndex);
      resetImageState();
      const newImage = imagesWithIds[newIndex];
      if (newImage && !newImage.isLoading && newImage.type !== 'viewpoint') {
        setMainImageLoading(true);
      }
    },
    [imagesWithIds, currentIndex, onChangeImage, resetImageState]
  );

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        handleImageNavigation('prev')();
      } else if (event.key === 'ArrowRight') {
        handleImageNavigation('next')();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, handleImageNavigation, imagesWithIds.length, currentIndex]);

  const handlePoiNavigation = (direction: 'next' | 'previous') => () => {
    setPoiNavigationActions({ type: direction });
    resetImageState();
    setMainImageLoading(true);
    setHasInitialized(false);
  };

  const handleThumbnailClick = (index: number) => {
    if (imagesWithIds[index].isLoading) return;

    onChangeImage(index);
    resetImageState();

    const targetImage = imagesWithIds[index];
    if (
      targetImage &&
      !targetImage.isLoading &&
      targetImage.type !== 'viewpoint' &&
      index !== currentIndex
    ) {
      setMainImageLoading(true);
    }
  };

  const handleImageLoad = () => {
    setMainImageLoading(false);
  };

  const handleImageError = () => {
    setMainImageLoading(false);
  };

  if (!open) return null;

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      {currentPoi && <DialogHeader currentPoi={currentPoi} onClose={onClose} />}

      <DialogContent sx={DialogContentStyle}>
        <MainContentContainer>
          <ImgNavButton
            onClick={handleImageNavigation('prev')}
            disabled={imagesWithIds.length <= 1}
            sx={{ left: '28px' }}
          >
            <ChevronLeft sx={{ fontSize: '32px' }} />
          </ImgNavButton>

          <ImgNavButton
            onClick={handleImageNavigation('next')}
            disabled={imagesWithIds.length <= 1}
            sx={{ right: '1.5rem' }}
          >
            <ChevronRight sx={{ fontSize: '32px' }} />
          </ImgNavButton>

          <PoiNavigationContainer>
            <IconButton
              onClick={handlePoiNavigation('previous')}
              size="small"
              disabled={allPois.length <= 1}
            >
              <ChevronLeft sx={{ color: abyssColors.primary[700], fontSize: '32px' }} />
            </IconButton>
            <CurrentPoiName variant="body1">
              {poiName}
            </CurrentPoiName>
            <IconButton
              onClick={handlePoiNavigation('next')}
              size="small"
              disabled={allPois.length <= 1}
            >
              <ChevronRight sx={{ color: abyssColors.primary[700], fontSize: '32px' }} />
            </IconButton>
          </PoiNavigationContainer>

          <MainImageSection
            currentImage={currentImage}
            mainImageLoading={mainImageLoading}
            imageTransform={imageTransform}
            imageEventHandlers={imageEventHandlers}
            handleImageLoad={handleImageLoad}
            handleImageError={handleImageError}
            canZoomIn={canZoomIn}
            canZoomOut={canZoomOut}
            handleZoom={handleZoom}
          />

          {/* TODO: The caption for the attachment images will be shown later. */}
          <ImageNumbering>
            {currentImage?.name
              ? `${currentImage.name} - ${currentIndex + 1}/${imagesWithIds.length}`
              : `${currentIndex + 1}/${imagesWithIds.length}`}
          </ImageNumbering>

          <ThumbnailList
            imagesWithIds={imagesWithIds}
            currentIndex={currentIndex}
            handleThumbnailClick={handleThumbnailClick}
          />
        </MainContentContainer>
        {currentPoi && <DetailsSidebar data={currentPoi} />}
      </DialogContent>
    </StyledDialog>
  );
};
